'use client';

import { useMemo } from 'react';
import { useRouter } from 'next/router';
import {
  useCreateMarketFilters,
  useCreateProductFilters,
  useCreateSegmentFilters,
} from '@/creditflowresearch/application/PageFilters/hooks/create';
import { HycProduct, IgcProduct, Product } from '@/creditflowresearch/domain/Product';
import Market, { EuropeanMarket, UsMarket } from '@/creditflowresearch/domain/Market';
import type { IFilterSet } from '@/creditflowresearch/ui/common/hooks/filters/useFilterButtons';
import { transformToFilterSets } from './helpers/transformToFilterSets';
import { SSA_SEGMENT_ID } from '@/creditflowresearch/domain/Segment/constants';
import { isSsaSegmentHidden } from '@/creditflowresearch/domain/Segment/selectors/selectSegmentVisibility';

export const useCorporateMarketFilters = (): IFilterSet[] => {
  const router = useRouter();
  const { query } = router;
  const allProducts = useCreateProductFilters();
  const allMarkets = useCreateMarketFilters();
  const allSegments = useCreateSegmentFilters();

  const products = useMemo(
    () => allProducts.filter(({ id }) =>
      Product.isOfType(id, HycProduct.TYPE) || Product.isOfType(id, IgcProduct.TYPE),
    ),
    [allProducts],
  );

  const markets = useMemo(
    () => allMarkets.filter(({ id }) =>
      id === UsMarket.MARKET_ID || id === EuropeanMarket.MARKET_ID,
    ),
    [allMarkets],
  );

  const isSsaHidden = isSsaSegmentHidden({
    productIds: Product.getIds(query.products ?? ''),
    marketIds: Market.getIds(query.markets ?? ''),
  });

  const segments = useMemo(
    () => allSegments.filter(({ id }) => id !== SSA_SEGMENT_ID || !isSsaHidden),
    [allSegments, isSsaHidden],
  );

  return transformToFilterSets([
    {
      key: 'segments',
      items: segments,
    },
    {
      key: 'markets',
      items: markets,
    },
    {
      key: 'products',
      items: products,
    },
  ], query);
};
